import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { signupAPI } from "../services/api";

const Success = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [verificationSentToClient, setVerificationSentToClient] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState("");
  
  // Edit email state
  const [isEditing, setIsEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [editError, setEditError] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const verifySession = async () => {
      if (!sessionId) {
        setStatus("error");
        setMessage("Invalid session. Please try signing up again.");
        return;
      }

      try {
        const response = await signupAPI.verifySession(sessionId);

        // Track signup conversion in Google Analytics
        if (window.gtag) {
          window.gtag('event', 'sign_up', { method: 'website' });
        }
        // Track signup conversion in Meta Pixel
        if (window.fbq) {
          window.fbq('track', 'CompleteRegistration');
        }

        // If tokens returned, auto-redirect to dashboard
        if (response.data.tokens) {
          const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL || "https://dashboard.secondclear.com";
          const { accessToken, refreshToken } = response.data.tokens;
          window.location.href = `${dashboardUrl}/auth-callback?accessToken=${encodeURIComponent(accessToken)}&refreshToken=${encodeURIComponent(refreshToken)}`;
          return;
        }

        // Fallback: show success page if no tokens yet (webhook still processing)
        setStatus("success");
        setEmail(response.data.email || "");
        setNewEmail(response.data.email || "");
        setVerificationSentToClient(response.data.verificationSentToClient || false);
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.error || "Something went wrong. Please contact support.");
      }
    };

    verifySession();
  }, [sessionId]);

  const handleResendEmail = async () => {
    setResending(true);
    setResent(false);
    setResendError("");
    try {
      await signupAPI.resendVerificationEmail(email);
      setResent(true);
      // Reset after 5 seconds so they can resend again if needed
      setTimeout(() => setResent(false), 5000);
    } catch (err) {
      console.error("Failed to resend email:", err);
      setResendError(err.response?.data?.error || "Failed to resend email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleEditEmail = () => {
    setIsEditing(true);
    setNewEmail(email);
    setEditError("");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewEmail(email);
    setEditError("");
  };

  const handleUpdateEmail = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEditError("Please enter a valid email address");
      return;
    }

    if (newEmail.toLowerCase() === email.toLowerCase()) {
      setIsEditing(false);
      return;
    }

    setUpdating(true);
    setEditError("");

    try {
      const response = await signupAPI.updateEmail(sessionId, newEmail);
      setEmail(response.data.email || newEmail);
      setIsEditing(false);
      setResent(false); // Reset resent state since email changed
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.code === "EMAIL_EXISTS") {
        setEditError(
          "This email is already associated with another account."
        );
      } else if (errorData?.code === "PROCESSING") {
        setEditError("Your account is still being set up. Please wait a moment and try again.");
      } else {
        setEditError(errorData?.error || "Failed to update email. Please try again.");
      }
    } finally {
      setUpdating(false);
    }
  };

  if (status === "loading") {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Setting up your account...</Typography>
        </Paper>
      </Container>
    );
  }

  if (status === "error") {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {message}
          </Alert>
          <Button
            variant="contained"
            fullWidth
            href="/"
          >
            Try Again
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: "success.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
        </Box>

        <Typography variant="h4" gutterBottom fontWeight={600}>
          You're All Set!
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Your account is ready. We've sent a verification email to:
        </Typography>

        {isEditing ? (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              error={!!editError}
              helperText={editError}
              disabled={updating}
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCancelEdit} disabled={updating} size="small">
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateEmail}
              disabled={updating}
              fullWidth
            >
              {updating ? <CircularProgress size={20} /> : "Update Email & Send Verification"}
            </Button>
          </Box>
        ) : (
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {email}
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleEditEmail}
              sx={{ color: "primary.main" }}
              title="Edit email address"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Verify your email to enable alerts and password reset. You can do this now or later from your dashboard.
        </Typography>

        <Button
          variant="contained"
          fullWidth
          size="large"
          href={process.env.REACT_APP_DASHBOARD_URL || "https://dashboard.secondclear.com"}
          endIcon={<ArrowForwardIcon />}
          sx={{ mb: 2 }}
        >
          Go to Your Dashboard
        </Button>

        {verificationSentToClient && (
          <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Verification Sent
            </Typography>
            <Typography variant="body2">
              We've also sent a verification text to their phone.
              They'll need to reply YES to activate their service.
            </Typography>
          </Alert>
        )}

        <Box sx={{ bgcolor: "#f5f7fa", p: 2, borderRadius: 1, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Didn't receive the verification email?</strong>
            <br />
            Check your spam folder, or click below to resend.
          </Typography>
        </Box>

        {resendError && (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {resendError}
          </Alert>
        )}

        <Button
          variant="outlined"
          fullWidth
          onClick={handleResendEmail}
          disabled={resending || resent || isEditing}
          sx={{ mb: 2 }}
        >
          {resent ? (
            <>
              <CheckCircleIcon sx={{ mr: 1 }} /> Email Sent!
            </>
          ) : resending ? (
            <CircularProgress size={20} />
          ) : (
            "Resend Verification Email"
          )}
        </Button>

        <Typography variant="body2" color="text.secondary">
          Need help? Contact us at{" "}
          <a href="mailto:support@secondclear.com">support@secondclear.com</a>
        </Typography>
      </Paper>
    </Container>
  );
};

export default Success;